// StarCoinsModal.jsx - Converted to full-width dropdown panel (keeping original props)
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, CreditCard, RefreshCcw } from 'lucide-react';
import { useUsageLimiter } from '../../utils/usageLimiter';
import { starCoinsClient } from '../../services/starCoinsClient';
import { detectUserCurrency, CURRENCY_INFO, calculatePrice, setUserCurrency } from '../../utils/detectLanguage';
import { trackInitiateStarCoinPurchase } from '../../utils/pixels';

const StarCoinsModal = ({ isOpen, onClose, userId }) => {
  const { starCoins, tier } = useUsageLimiter(userId);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showTransactions, setShowTransactions] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

    // ADD THESE NEW STATES:
  const [currency, setCurrency] = useState('usd');
  const [showCurrencyOption, setShowCurrencyOption] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

useEffect(() => {
  if (isOpen) {
    console.log('[StarCoinsModal] Modal opened, detecting currency...');

    // Check if mobile view
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    checkMobileView();
    window.addEventListener('resize', checkMobileView);

    // Detect user's preferred currency based on locale
    const detectedCurrency = detectUserCurrency();
    console.log('[StarCoinsModal] Detected currency:', detectedCurrency);

    // Always show currency selector to allow manual switching
    setShowCurrencyOption(true);

    // Set currency to detected value
    setCurrency(detectedCurrency);
    console.log('[StarCoinsModal] Currency set to:', detectedCurrency);

    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }
}, [isOpen]);

  // Load packages and transactions when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError(null);
      
      // Load packages
      starCoinsClient.getPackages(tier === 'pro')
        .then(packages => {
          console.log('Loaded packages:', packages);
          setPackages(packages);
          
          // If no package is selected yet, select the first one
          if (!selectedPackage && packages.length > 0) {
            setSelectedPackage(packages[0].id);
          }
        })
        .catch(error => {
          console.error('Error loading packages:', error);
          setError('Failed to load packages. Please try again.');
        })
        .finally(() => setIsLoading(false));
      
      // Load transaction history
      starCoinsClient.getTransactionHistory()
        .then(transactions => setTransactions(transactions))
        .catch(error => {
          console.error('Error loading transactions:', error);
          // Not setting modal error for this since it's not critical
        });
    }
  }, [isOpen, tier]);

  // Reset pagination when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen]);

  // USD ONLY...
  // const handlePurchase = async () => {
  //   if (!selectedPackage) return;
    
  //   setIsProcessing(true);
  //   setError(null);
    
  //   try {
  //     // Find the selected package
  //     const packageData = packages.find(p => p.id === selectedPackage);
  //     if (!packageData) {
  //       throw new Error('Package not found');
  //     }
      
  //     // Use the starCoinsClient to create a checkout
  //     await starCoinsClient.createCheckout(
  //       packageData.id,
  //       packageData.amount,
  //       packageData.price,
  //       tier === 'pro'
  //     );
      
  //     // Note: We'll be redirected to Stripe checkout, no need to handle success here
  //   } catch (error) {
  //     console.error('Error processing payment:', error);
  //     setIsProcessing(false);
  //     setError('Error processing payment. Please try again.');
  //   }
  // };
const handlePurchase = async () => {
  if (!selectedPackage) return;
  
  setIsProcessing(true);
  setError(null);
  
  try {
    // Find the selected package
    const packageData = packages.find(p => p.id === selectedPackage);
    if (!packageData) {
      throw new Error('Package not found');
    }

    // Calculate price in selected currency (uses Firestore custom price or fallback calculation)
    const price = calculatePrice(packageData, currency);

    // Track initiate checkout event for Facebook Pixel
    trackInitiateStarCoinPurchase(packageData.id, price);
    
    // Use the starCoinsClient to create a checkout with currency
    await starCoinsClient.createCheckout(
      packageData.id,
      packageData.amount,
      price, // Use converted price
      tier === 'pro',
      currency // ADD CURRENCY PARAMETER
    );
    
    // Note: We'll be redirected to Stripe checkout, no need to handle success here
  } catch (error) {
    console.error('Error processing payment:', error);
    setIsProcessing(false);
    setError('Error processing payment. Please try again.');
  }
};

  // Pagination calculations
  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = transactions.slice(startIndex, startIndex + itemsPerPage);
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-40"
            onClick={onClose}
          />

          {/* Top sheet panel */}
          <motion.div
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 300,
              mass: 0.8
            }}
            className="fixed left-0 right-0 top-0 z-50 bg-black/90 backdrop-blur-lg border-b border-white/10 shadow-2xl rounded-b-lg overflow-y-auto"
            style={{ height: 'auto', maxHeight: '100vh' }}
          >
          <div
            className="absolute inset-0 pointer-events-none overflow-hidden"
            style={{
              backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(16, 185, 129, 0.02) 0%, transparent 25%), radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.02) 0%, transparent 30%)',
            }}
          />

          <div className="w-full max-w-4xl mx-auto px-6 py-4 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center h-10 w-10 bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded shadow-lg">
                  <Star className="w-5 h-5 text-emerald-400/80" />
                </div>
                <div>
                  <h3 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase">
                    Star Coins
                  </h3>
                  <p className="text-sm text-white/50 mt-0.5">{currency === 'brl' ? 'Continue usando recursos após atingir os limites' : 'Continue using features after reaching limits'}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-md text-[11px] text-white/40 uppercase tracking-wider hover:text-white/70 bg-white/[0.02] hover:bg-white/[0.03] border border-white/[0.05] hover:border-white/[0.08] transition-all duration-200"
              >
                Close
              </button>
            </div>

            <div
              className="h-px w-full mb-4"
              style={{
                background: 'linear-gradient(to right, transparent, rgba(16, 185, 129, 0.2), rgba(139, 92, 246, 0.2), transparent)'
              }}
            />
            
            {/* Content area */}
            <div className="bg-white/[0.02] backdrop-blur-sm rounded-lg border border-white/[0.05] shadow-inner">
              <div className="p-6">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Current Balance */}
                  <div className="mb-6 p-4 rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.05]">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] text-white/30 uppercase tracking-wider mb-1">{currency === 'brl' ? 'Saldo Atual' : 'Current Balance'}</div>
                        <div className="flex items-center text-emerald-400/80 font-mono font-medium text-2xl">
                          <Star className="w-5 h-5 mr-2" />
                          <span>{starCoins.balance || 0}</span>
                        </div>
                      </div>
                      {tier === 'pro' ? (
                        <div className="text-emerald-400/80 text-[11px] font-medium uppercase tracking-wider px-3 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/20">
                          Pro Bonuses Active
                        </div>
                      ) : (
                        <div className="text-white/80 text-xs font-medium px-4 py-2 rounded-md bg-gradient-to-r from-emerald-500/10 to-violet-500/10 border border-violet-400/20 shadow-lg">
                          Subscribe to PRO for bonus coins
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Tabs */}
                  <div className="mb-6 border-b border-white/[0.05]">
                    <div className="flex gap-8">
                      <button
                        onClick={() => setShowTransactions(false)}
                        className={`relative pb-3 text-[11px] font-medium uppercase tracking-[0.15em] transition-colors duration-200 ${
                          !showTransactions ? 'text-white/80' : 'text-white/40 hover:text-white/60'
                        }`}
                      >
                        <span>{currency === 'brl' ? 'Comprar Moedas' : 'Purchase Coins'}</span>
                        {!showTransactions && (
                          <motion.div
                            layoutId="tabIndicator"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 to-violet-400"
                          />
                        )}
                      </button>

                      <button
                        onClick={() => setShowTransactions(true)}
                        className={`relative pb-3 text-[11px] font-medium uppercase tracking-[0.15em] transition-colors duration-200 ${
                          showTransactions ? 'text-white/80' : 'text-white/40 hover:text-white/60'
                        }`}
                      >
                        <span>{currency === 'brl' ? 'Histórico' : 'History'}</span>
                        {showTransactions && (
                          <motion.div
                            layoutId="tabIndicator"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 to-violet-400"
                          />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Tab Content */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={showTransactions ? 'transactions' : 'purchase'}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {!showTransactions ? (
                        <div>
                          {isLoading ? (
                            <div className="flex justify-center py-10">
                              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-400"></div>
                            </div>
                          ) : error ? (
                            <div className="text-center py-6 text-red-400">
                              {error}
                            </div>
                          ) : (
                            <div>
                          {/* Currency Selection - Professional Flag Buttons */}
                          <div className="mb-6 p-3 rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.05]">
                            <h4 className="text-[10px] text-white/30 uppercase tracking-wider mb-3 text-center">
                              Currency
                            </h4>
                            <div className="flex flex-wrap justify-center gap-2">
                              <button
                                onClick={() => { setCurrency('usd'); setUserCurrency('usd'); }}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all ${
                                  currency === 'usd'
                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-lg shadow-emerald-500/10'
                                    : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05] hover:text-white/80 hover:border-white/[0.15]'
                                }`}
                              >
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 overflow-hidden text-sm">🇺🇸</span>
                                <span className="text-xs font-medium">USD</span>
                              </button>
                              <button
                                onClick={() => { setCurrency('brl'); setUserCurrency('brl'); }}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all ${
                                  currency === 'brl'
                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-lg shadow-emerald-500/10'
                                    : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05] hover:text-white/80 hover:border-white/[0.15]'
                                }`}
                              >
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 overflow-hidden text-sm">🇧🇷</span>
                                <span className="text-xs font-medium">BRL</span>
                              </button>
                              <button
                                onClick={() => { setCurrency('mxn'); setUserCurrency('mxn'); }}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all ${
                                  currency === 'mxn'
                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-lg shadow-emerald-500/10'
                                    : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05] hover:text-white/80 hover:border-white/[0.15]'
                                }`}
                              >
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 overflow-hidden text-sm">🇲🇽</span>
                                <span className="text-xs font-medium">MXN</span>
                              </button>
                              <button
                                onClick={() => { setCurrency('ars'); setUserCurrency('ars'); }}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all ${
                                  currency === 'ars'
                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-lg shadow-emerald-500/10'
                                    : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05] hover:text-white/80 hover:border-white/[0.15]'
                                }`}
                              >
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 overflow-hidden text-sm">🇦🇷</span>
                                <span className="text-xs font-medium">ARS</span>
                              </button>
                              <button
                                onClick={() => { setCurrency('cop'); setUserCurrency('cop'); }}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all ${
                                  currency === 'cop'
                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-lg shadow-emerald-500/10'
                                    : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05] hover:text-white/80 hover:border-white/[0.15]'
                                }`}
                              >
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 overflow-hidden text-sm">🇨🇴</span>
                                <span className="text-xs font-medium">COP</span>
                              </button>
                              <button
                                onClick={() => { setCurrency('clp'); setUserCurrency('clp'); }}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all ${
                                  currency === 'clp'
                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-lg shadow-emerald-500/10'
                                    : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05] hover:text-white/80 hover:border-white/[0.15]'
                                }`}
                              >
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 overflow-hidden text-sm">🇨🇱</span>
                                <span className="text-xs font-medium">CLP</span>
                              </button>
                            </div>
                            <div className="mt-2 text-[10px] text-white/40 text-center">
                              {currency === 'brl' && '💳 PIX + Cards'}
                              {currency === 'mxn' && '💳 Cards Only'}
                              {!['brl', 'mxn'].includes(currency) && '💳 Cards Only'}
                            </div>
                          </div>

                          {/* Packages Grid - UPDATE TO SHOW CURRENCY */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            {packages.map(pkg => {
                              const isActive = selectedPackage === pkg.id;
                              const bonusAmount = pkg.proBonus ? pkg.amount - (pkg.originalAmount || pkg.amount * 0.8) : 0;

                              // Calculate display price based on currency
                              // Calculate display price for this package
                              const displayPrice = calculatePrice(pkg, currency);
                              const currencySymbol = CURRENCY_INFO[currency]?.symbol || '$';

                              return (
                                <div
                                  key={pkg.id}
                                  className={`relative p-4 rounded-lg cursor-pointer transition-all duration-200 group ${
                                    isActive
                                      ? 'bg-gradient-to-r from-emerald-500/10 to-violet-500/10 border border-emerald-500/30'
                                      : 'bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08]'
                                  }`}
                                  onClick={() => setSelectedPackage(pkg.id)}
                                >
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <div className="flex items-center text-emerald-400/80 text-lg font-mono font-medium group-hover:text-emerald-400/90 transition-colors">
                                        <Star className="w-5 h-5 mr-2" />
                                        <span>{pkg.amount >= 1000 ? `${pkg.amount/1000}k` : pkg.amount} {currency === 'brl' ? 'moedas' : 'coins'}</span>
                                      </div>
                                      {bonusAmount > 0 && (
                                        <div className="text-emerald-400/70 text-[10px] font-mono uppercase tracking-wider mt-1">
                                          +{Math.round(bonusAmount)} {currency === 'brl' ? 'bônus' : 'bonus'}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      {/* PROMOTIONAL STRIKETHROUGH - Remove this block to disable promotion */}
                                      {currency === 'brl' && pkg.brlPrice && pkg.brlPrice === 2 && (
                                        <>
                                          <div className="text-white/30 text-xs font-mono line-through">R$5,00</div>
                                          <div className="text-emerald-400/70 text-[9px] uppercase tracking-wider">Até 15 de Jan</div>
                                        </>
                                      )}
                                      {/* END PROMOTIONAL STRIKETHROUGH */}

                                      <div className="text-white/80 font-mono font-medium text-lg group-hover:text-white/90 transition-colors">{currencySymbol}{displayPrice.toFixed(2)}</div>
                                      {pkg.proBonus && (
                                        <div className="text-emerald-400/70 text-[10px] uppercase tracking-wider">{currency === 'brl' ? 'Bônus Pro' : 'Pro Bonus'}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                              
                              {/* Purchase Button */}
                              <button
                                onClick={handlePurchase}
                                disabled={!selectedPackage || isProcessing}
                                className="w-full py-4 rounded-md flex items-center justify-center gap-2 text-white font-medium text-[11px] uppercase tracking-[0.15em] bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-emerald-500/10"
                              >
                                {isProcessing ? (
                                  <>
                                    <RefreshCcw className="w-4 h-4 animate-spin" />
                                    <span>{currency === 'brl' ? 'Processando...' : 'Processing...'}</span>
                                  </>
                                ) : (
                                  <>
                                    <CreditCard className="w-4 h-4" />
                                    <span>{currency === 'brl' ? 'Comprar Coins' : 'Purchase Coins'}</span>
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          {transactions.length > 0 ? (
                            <>
                              {/* Transaction display with pagination */}
                              <div className="space-y-3 mb-6">
                                {paginatedTransactions.map((transaction, index) => (
                                  <div key={transaction.id || index} className="flex items-center justify-between p-4 bg-white/[0.02] backdrop-blur-sm rounded-lg border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <div className={`text-sm font-mono font-medium ${
                                          transaction.amount > 0
                                            ? 'text-emerald-400/80 group-hover:text-emerald-400/90'
                                            : 'text-red-400/70 group-hover:text-red-400/80'
                                        }`}>
                                          {transaction.amount > 0 ? '+' : ''}{transaction.amount} coins
                                        </div>
                                      </div>
                                      <div className="text-xs text-white/50 mt-1">
                                        {transaction.feature || transaction.source || 'System'}
                                      </div>
                                    </div>

                                    <div className="text-right text-[10px] font-mono text-white/30 uppercase tracking-wider">
                                      {new Date(transaction.timestamp).toLocaleDateString()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Pagination controls - only show if more than one page */}
                              {totalPages > 1 && (
                                <div className="flex items-center justify-between pt-4 border-t border-white/[0.05]">
                                  <div className="text-[10px] font-mono text-white/30 uppercase tracking-wider">
                                    {startIndex + 1}-{Math.min(startIndex + itemsPerPage, transactions.length)} of {transactions.length}
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                      disabled={currentPage === 1}
                                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider rounded-md border border-white/[0.05] text-white/40 hover:text-white/70 hover:bg-white/[0.02] hover:border-white/[0.08] disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                                    >
                                      Previous
                                    </button>

                                    <div className="flex items-center gap-1">
                                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                          key={page}
                                          onClick={() => setCurrentPage(page)}
                                          className={`w-8 h-8 text-[10px] font-mono rounded transition-all duration-200 ${
                                            currentPage === page
                                              ? 'bg-gradient-to-r from-emerald-500/20 to-violet-500/20 text-white/80 border border-emerald-500/30'
                                              : 'text-white/40 hover:text-white/70 hover:bg-white/[0.02]'
                                          }`}
                                        >
                                          {page}
                                        </button>
                                      ))}
                                    </div>

                                    <button
                                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                      disabled={currentPage === totalPages}
                                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider rounded-md border border-white/[0.05] text-white/40 hover:text-white/70 hover:bg-white/[0.02] hover:border-white/[0.08] disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                                    >
                                      Next
                                    </button>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center py-12">
                              <div className="inline-flex items-center justify-center h-14 w-14 bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg mb-4">
                                <Star className="w-7 h-7 text-emerald-400/50" />
                              </div>
                              <p className="text-white/70 text-sm">{currency === 'brl' ? 'Nenhuma transação ainda' : 'No transactions yet'}</p>
                              <p className="text-white/40 text-xs uppercase tracking-wider mt-2">{currency === 'brl' ? 'Suas compras aparecerão aqui' : 'Your purchases will appear here'}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default StarCoinsModal;



// WORKING // StarCoinsModal.jsx - Converted to full-width dropdown panel (keeping original props)
// import React, { useState, useEffect } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { X, Star, CreditCard, RefreshCcw } from 'lucide-react';
// import { useUsageLimiter } from '../../utils/usageLimiter';
// import { starCoinsClient } from '../../services/starCoinsClient';

// const StarCoinsModal = ({ isOpen, onClose, userId }) => {
//   const { starCoins, tier } = useUsageLimiter(userId);
//   const [selectedPackage, setSelectedPackage] = useState(null);
//   const [showTransactions, setShowTransactions] = useState(false);
//   const [isProcessing, setIsProcessing] = useState(false);
//   const [packages, setPackages] = useState([]);
//   const [transactions, setTransactions] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState(null);

//   // Load packages and transactions when modal opens
//   useEffect(() => {
//     if (isOpen) {
//       setIsLoading(true);
//       setError(null);
      
//       // Load packages
//       starCoinsClient.getPackages(tier === 'pro')
//         .then(packages => {
//           console.log('Loaded packages:', packages);
//           setPackages(packages);
          
//           // If no package is selected yet, select the first one
//           if (!selectedPackage && packages.length > 0) {
//             setSelectedPackage(packages[0].id);
//           }
//         })
//         .catch(error => {
//           console.error('Error loading packages:', error);
//           setError('Failed to load packages. Please try again.');
//         })
//         .finally(() => setIsLoading(false));
      
//       // Load transaction history
//       starCoinsClient.getTransactionHistory()
//         .then(transactions => setTransactions(transactions))
//         .catch(error => {
//           console.error('Error loading transactions:', error);
//           // Not setting modal error for this since it's not critical
//         });
//     }
//   }, [isOpen, tier]);

//   const handlePurchase = async () => {
//     if (!selectedPackage) return;
    
//     setIsProcessing(true);
//     setError(null);
    
//     try {
//       // Find the selected package
//       const packageData = packages.find(p => p.id === selectedPackage);
//       if (!packageData) {
//         throw new Error('Package not found');
//       }
      
//       // Use the starCoinsClient to create a checkout
//       await starCoinsClient.createCheckout(
//         packageData.id,
//         packageData.amount,
//         packageData.price,
//         tier === 'pro'
//       );
      
//       // Note: We'll be redirected to Stripe checkout, no need to handle success here
//     } catch (error) {
//       console.error('Error processing payment:', error);
//       setIsProcessing(false);
//       setError('Error processing payment. Please try again.');
//     }
//   };
  
//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <motion.div
//           initial={{ opacity: 0, height: 0 }}
//           animate={{ opacity: 1, height: 'auto' }}
//           exit={{ opacity: 0, height: 0 }}
//           transition={{ duration: 0.25, ease: 'easeInOut' }}
//           className="fixed left-0 right-0 top-[57px] z-40 bg-gradient-to-b from-slate-800/95 to-slate-900/95 backdrop-blur-xl border-b border-white/10 shadow-2xl overflow-hidden"
//           style={{ position: 'absolute', top: '57px' }}
//         >
//           <div 
//             className="absolute inset-0 pointer-events-none overflow-hidden"
//             style={{ 
//               backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(251, 191, 36, 0.03) 0%, transparent 25%), radial-gradient(circle at 80% 70%, rgba(124, 58, 237, 0.03) 0%, transparent 30%)',
//             }}
//           />
          
//           <div className="w-full max-w-4xl mx-auto px-4 py-5">
//             <div className="flex items-center justify-between mb-4">
//               <div className="flex items-center gap-3">
//                 <div className="flex items-center justify-center h-9 w-9 bg-gradient-to-br from-amber-500/30 to-orange-600/30 rounded-xl shadow-lg">
//                   <Star className="w-5 h-5 text-white drop-shadow-md" />
//                 </div>
//                 <div>
//                   <h3 className="text-xl font-medium text-white">
//                     <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-300">Star Coins</span>
//                   </h3>
//                   <p className="text-sm text-white/50 mt-0.5">Continue using features after reaching limits</p>
//                 </div>
//               </div>
//               <button 
//                 onClick={onClose}
//                 className="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-white/70 hover:text-white bg-white/5 hover:bg-white/10 transition-colors border border-white/5 shadow-sm"
//               >
//                 <span className="text-sm font-medium">Close</span>
//               </button>
//             </div>
            
//             <div 
//               className="h-px w-full mb-5"
//               style={{
//                 background: 'linear-gradient(to right, transparent, rgba(251, 191, 36, 0.2), rgba(124, 58, 237, 0.2), transparent)'
//               }}
//             />
            
//             {/* Content area */}
//             <div className="bg-gradient-to-br from-slate-800/70 to-slate-900/70 rounded-xl border border-white/5 shadow-inner backdrop-blur-sm">
//               <div className="p-5">
//                 <motion.div
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ duration: 0.2 }}
//                 >
//                   {/* Current Balance */}
//                   <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20">
//                     <div className="flex items-center justify-between">
//                       <div>
//                         <div className="flex items-center text-amber-300 font-medium text-xl">
//                           <Star className="w-5 h-5 mr-2" />
//                           <span>{starCoins.balance || 0}</span>
//                         </div>
//                         <div className="text-white/60 text-sm mt-1">Current Balance</div>
//                       </div>
//                       {tier === 'pro' && (
//                         <div className="text-emerald-400 text-sm font-medium px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
//                           Pro Bonuses Active
//                         </div>
//                       )}
//                     </div>
//                   </div>
                  
//                   {/* Tabs */}
//                   <div className="mb-5">
//                     <div className="flex space-x-8">
//                       <button
//                         onClick={() => setShowTransactions(false)}
//                         className={`relative pb-2 text-sm font-medium transition-colors ${
//                           !showTransactions ? 'text-amber-400' : 'text-slate-400 hover:text-white'
//                         }`}
//                       >
//                         <span>Purchase Coins</span>
//                         {!showTransactions && (
//                           <motion.div 
//                             layoutId="tabIndicator"
//                             className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400 to-orange-400"
//                           />
//                         )}
//                       </button>
                      
//                       <button
//                         onClick={() => setShowTransactions(true)}
//                         className={`relative pb-2 text-sm font-medium transition-colors ${
//                           showTransactions ? 'text-amber-400' : 'text-slate-400 hover:text-white'
//                         }`}
//                       >
//                         <span>Transaction History</span>
//                         {showTransactions && (
//                           <motion.div 
//                             layoutId="tabIndicator"
//                             className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400 to-orange-400"
//                           />
//                         )}
//                       </button>
//                     </div>
//                   </div>
                  
//                   {/* Tab Content */}
//                   <AnimatePresence mode="wait">
//                     <motion.div
//                       key={showTransactions ? 'transactions' : 'purchase'}
//                       initial={{ opacity: 0, y: 10 }}
//                       animate={{ opacity: 1, y: 0 }}
//                       exit={{ opacity: 0, y: -10 }}
//                       transition={{ duration: 0.2 }}
//                     >
//                       {!showTransactions ? (
//                         <div>
//                           {isLoading ? (
//                             <div className="flex justify-center py-10">
//                               <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-400"></div>
//                             </div>
//                           ) : error ? (
//                             <div className="text-center py-6 text-red-400">
//                               {error}
//                             </div>
//                           ) : (
//                             <div>
//                               {/* Packages Grid */}
//                               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
//                                 {packages.map(pkg => {
//                                   const isActive = selectedPackage === pkg.id;
//                                   const bonusAmount = pkg.proBonus ? pkg.amount - (pkg.originalAmount || pkg.amount * 0.8) : 0;
                                  
//                                   return (
//                                     <div 
//                                       key={pkg.id}
//                                       className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
//                                         isActive 
//                                           ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/40' 
//                                           : 'bg-slate-700/30 border border-slate-600/50 hover:border-slate-500/80'
//                                       }`}
//                                       onClick={() => setSelectedPackage(pkg.id)}
//                                     >
//                                       <div className="flex justify-between items-center">
//                                         <div>
//                                           <div className="flex items-center text-amber-300 text-lg font-medium">
//                                             <Star className="w-5 h-5 mr-2" />
//                                             <span>{pkg.amount >= 1000 ? `${pkg.amount/1000}k` : pkg.amount} coins</span>
//                                           </div>
//                                           {bonusAmount > 0 && (
//                                             <div className="text-emerald-400 text-xs mt-1">
//                                               +{Math.round(bonusAmount)} bonus coins
//                                             </div>
//                                           )}
//                                         </div>
//                                         <div className="text-right">
//                                           <div className="text-white font-medium text-lg">${pkg.price.toFixed(2)}</div>
//                                           {pkg.proBonus && (
//                                             <div className="text-emerald-400 text-xs">Pro Bonus</div>
//                                           )}
//                                         </div>
//                                       </div>
//                                     </div>
//                                   );
//                                 })}
//                               </div>
                              
//                               {/* Purchase Button */}
//                               <button
//                                 onClick={handlePurchase}
//                                 disabled={!selectedPackage || isProcessing}
//                                 className="w-full py-3 rounded-lg flex items-center justify-center gap-2 text-white font-medium bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
//                               >
//                                 {isProcessing ? (
//                                   <>
//                                     <RefreshCcw className="w-4 h-4 animate-spin" />
//                                     <span>Processing...</span>
//                                   </>
//                                 ) : (
//                                   <>
//                                     <CreditCard className="w-4 h-4" />
//                                     <span>Purchase Star Coins</span>
//                                   </>
//                                 )}
//                               </button>
//                             </div>
//                           )}
//                         </div>
//                       ) : (
//                         <div>
//                           {transactions.length > 0 ? (
//                             <div className="space-y-3">
//                               {transactions.map((transaction, index) => (
//                                 <div key={transaction.id || index} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
//                                   <div>
//                                     <div className="flex items-center gap-2">
//                                       <div className={`text-base font-medium ${
//                                         transaction.amount > 0 
//                                           ? 'text-emerald-300' 
//                                           : 'text-red-300'
//                                       }`}>
//                                         {transaction.amount > 0 ? '+' : ''}{transaction.amount} coins
//                                       </div>
//                                     </div>
//                                     <div className="text-sm text-white/50 mt-1">
//                                       {transaction.feature || transaction.source || 'System'}
//                                     </div>
//                                   </div>
                                  
//                                   <div className="text-right text-sm text-white/50">
//                                     {new Date(transaction.timestamp).toLocaleDateString()}
//                                   </div>
//                                 </div>
//                               ))}
//                             </div>
//                           ) : (
//                             <div className="text-center py-10">
//                               <div className="inline-flex items-center justify-center h-12 w-12 bg-amber-500/10 rounded-full mb-3">
//                                 <Star className="w-6 h-6 text-amber-400/70" />
//                               </div>
//                               <p className="text-white/70 text-base">No transactions yet</p>
//                               <p className="text-white/50 text-sm mt-1">Your coin purchases will appear here</p>
//                             </div>
//                           )}
//                         </div>
//                       )}
//                     </motion.div>
//                   </AnimatePresence>
//                 </motion.div>
//               </div>
//             </div>
//           </div>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// };

// export default StarCoinsModal;


// // StarCoinsModal.jsx - Fixed vertical centering
// import React, { useState, useEffect } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { X, Star, CreditCard, RefreshCcw } from 'lucide-react';
// import { useUsageLimiter } from '../../utils/usageLimiter';
// import { starCoinsClient } from '../../services/starCoinsClient';

// const StarCoinsModal = ({ isOpen, onClose, userId }) => {
//   const { starCoins, tier } = useUsageLimiter(userId);
//   const [selectedPackage, setSelectedPackage] = useState(null);
//   const [showTransactions, setShowTransactions] = useState(false);
//   const [isProcessing, setIsProcessing] = useState(false);
//   const [packages, setPackages] = useState([]);
//   const [transactions, setTransactions] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState(null);

//   // Load packages and transactions when modal opens
//   useEffect(() => {
//     if (isOpen) {
//       setIsLoading(true);
//       setError(null);
      
//       // Load packages
//       starCoinsClient.getPackages(tier === 'pro')
//         .then(packages => {
//           console.log('Loaded packages:', packages);
//           setPackages(packages);
          
//           // If no package is selected yet, select the first one
//           if (!selectedPackage && packages.length > 0) {
//             setSelectedPackage(packages[0].id);
//           }
//         })
//         .catch(error => {
//           console.error('Error loading packages:', error);
//           setError('Failed to load packages. Please try again.');
//         })
//         .finally(() => setIsLoading(false));
      
//       // Load transaction history
//       starCoinsClient.getTransactionHistory()
//         .then(transactions => setTransactions(transactions))
//         .catch(error => {
//           console.error('Error loading transactions:', error);
//           // Not setting modal error for this since it's not critical
//         });
//     }
//   }, [isOpen, tier]);

//   const handlePurchase = async () => {
//     if (!selectedPackage) return;
    
//     setIsProcessing(true);
//     setError(null);
    
//     try {
//       // Find the selected package
//       const packageData = packages.find(p => p.id === selectedPackage);
//       if (!packageData) {
//         throw new Error('Package not found');
//       }
      
//       // Use the starCoinsClient to create a checkout
//       await starCoinsClient.createCheckout(
//         packageData.id,
//         packageData.amount,
//         packageData.price,
//         tier === 'pro'
//       );
      
//       // Note: We'll be redirected to Stripe checkout, no need to handle success here
//     } catch (error) {
//       console.error('Error processing payment:', error);
//       setIsProcessing(false);
//       setError('Error processing payment. Please try again.');
//     }
//   };
  
//   return (
//     <AnimatePresence>
//       {isOpen && (
//         // CSS positioning for exact center of the viewport
//         <div 
//           className="fixed inset-0 flex items-center justify-center z-[100]" 
//           style={{
//             background: 'rgba(0, 0, 0, 0.7)',
//             backdropFilter: 'blur(4px)'
//           }}
//           onClick={onClose}
//         >
//           <motion.div
//             initial={{ opacity: 0, y: 20, scale: 0.95 }}
//             animate={{ opacity: 1, y: 0, scale: 1 }}
//             exit={{ opacity: 0, y: 20, scale: 0.95 }}
//             transition={{ type: 'spring', damping: 25, stiffness: 300 }}
//             className="bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl w-full max-w-md"
//             style={{ 
//               position: 'absolute',
//               top: '50%',
//               left: '50%',
//               transform: 'translate(-50%, -50%)',
//               maxHeight: '80vh',
//               overflow: 'hidden'
//             }}
//             onClick={e => e.stopPropagation()}
//           >
//             {/* Modal content */}
//             <div className="flex items-center justify-between p-4 pb-3 bg-slate-800">
//               <div className="flex items-center gap-2">
//                 <div className="flex items-center justify-center">
//                   <Star className="w-5 h-5 text-amber-400" />
//                 </div>
//                 <div>
//                   <h3 className="text-lg font-medium text-white">Star Coins</h3>
//                   <p className="text-xs text-white/60">Continue using features after reaching limits</p>
//                 </div>
//               </div>
//               <button
//                 onClick={onClose}
//                 className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
//               >
//                 <X className="w-5 h-5" />
//               </button>
//             </div>
            
//             {/* Current Balance Box */}
//             <div className="px-4 py-3 mb-1">
//               <div className="flex items-center text-amber-300 font-medium">
//                 <Star className="w-4 h-4 mr-1.5" />
//                 <span className="text-lg mr-1">{starCoins.balance || 0}</span>
//                 <span className="text-sm text-emerald-400 ml-2">
//                   {tier === 'pro' && "(includes Pro Bonus)"}
//                 </span>
//               </div>
//               <div className="text-white/60 text-sm mt-1">Current Balance</div>
//             </div>
            
//             {/* Tabs */}
//             <div className="border-b border-slate-700/50 flex">
//               <button
//                 onClick={() => setShowTransactions(false)}
//                 className={`flex-1 py-2 text-center text-sm font-medium ${
//                   !showTransactions ? 'text-amber-400 border-b-2 border-amber-400' : 'text-slate-400'
//                 }`}
//               >
//                 Purchase Coins
//               </button>
//               <button
//                 onClick={() => setShowTransactions(true)}
//                 className={`flex-1 py-2 text-center text-sm font-medium ${
//                   showTransactions ? 'text-amber-400 border-b-2 border-amber-400' : 'text-slate-400'
//                 }`}
//               >
//                 Transaction History
//               </button>
//             </div>
            
//             {/* Content area */}
//             <div style={{ maxHeight: 'calc(80vh - 220px)', overflow: 'auto' }}>
//               {!showTransactions ? (
//                 <div>
//                   {isLoading ? (
//                     <div className="flex justify-center py-10">
//                       <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-400"></div>
//                     </div>
//                   ) : (
//                     <div>
//                       {packages.map(pkg => {
//                         const isActive = selectedPackage === pkg.id;
//                         const bonusAmount = pkg.proBonus ? pkg.amount - (pkg.originalAmount || pkg.amount * 0.8) : 0;
                        
//                         return (
//                           <div 
//                             key={pkg.id}
//                             className={`py-4 px-4 border-b border-slate-700/30 cursor-pointer transition-colors ${
//                               isActive ? 'bg-slate-700/30' : 'hover:bg-slate-700/20'
//                             }`}
//                             onClick={() => setSelectedPackage(pkg.id)}
//                           >
//                             <div className="flex justify-between items-center">
//                               <div>
//                                 {pkg.amount >= 1000 ? (
//                                   <div className="text-base text-white mb-1">{pkg.amount/1000}k Star Coins</div>
//                                 ) : null}
//                                 <div className="flex items-center text-amber-300">
//                                   <Star className="w-4 h-4 mr-1" />
//                                   <span className="font-medium">{pkg.amount} coins</span>
//                                   {bonusAmount > 0 && (
//                                     <span className="text-xs text-emerald-400 ml-1">
//                                       (includes {Math.round(bonusAmount)} bonus)
//                                     </span>
//                                   )}
//                                 </div>
//                               </div>
//                               <div className="text-right">
//                                 <div className="text-white font-medium">${pkg.price.toFixed(2)}</div>
//                                 {pkg.proBonus && (
//                                   <div className="text-emerald-400 text-xs">Pro Bonus</div>
//                                 )}
//                               </div>
//                             </div>
//                           </div>
//                         );
//                       })}
//                     </div>
//                   )}
//                 </div>
//               ) : (
//                 <div>
//                   {transactions.length > 0 ? (
//                     <div className="divide-y divide-slate-700/30">
//                       {transactions.map((transaction, index) => (
//                         <div key={transaction.id || index} className="flex items-center justify-between p-4">
//                           <div>
//                             <div className="flex items-center gap-1.5">
//                               <div className={`text-sm ${
//                                 transaction.amount > 0 
//                                   ? 'text-emerald-300' 
//                                   : 'text-red-300'
//                               }`}>
//                                 {transaction.amount > 0 ? '+' : ''}{transaction.amount} coins
//                               </div>
//                             </div>
//                             <div className="text-xs text-white/50 mt-1">
//                               {transaction.feature || transaction.source || 'System'}
//                             </div>
//                           </div>
                          
//                           <div className="text-right text-xs text-white/50">
//                             {new Date(transaction.timestamp).toLocaleDateString()}
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   ) : (
//                     <div className="text-center py-6 text-white/50">
//                       No transactions yet
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
            
//             {/* Purchase button */}
//             {!showTransactions && (
//               <div className="p-4">
//                 <button
//                   onClick={handlePurchase}
//                   disabled={!selectedPackage || isProcessing}
//                   className="w-full py-3 rounded-lg flex items-center justify-center gap-2 text-white font-medium bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
//                 >
//                   {isProcessing ? (
//                     <>
//                       <RefreshCcw className="w-4 h-4 animate-spin" />
//                       <span>Processing...</span>
//                     </>
//                   ) : (
//                     <>
//                       <CreditCard className="w-4 h-4" />
//                       <span>Purchase</span>
//                     </>
//                   )}
//                 </button>
//               </div>
//             )}
//           </motion.div>
//         </div>
//       )}
//     </AnimatePresence>
//   );
// };

// export default StarCoinsModal;

// first to work with stripe and coin updates
// // StarCoinsModal.jsx - Fixed version
// import React, { useState, useEffect } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { X, Star, CreditCard, Check, RefreshCcw } from 'lucide-react';
// import { useUsageLimiter } from '../../utils/usageLimiter';
// import { starCoinsClient } from '../../services/starCoinsClient';

// const StarCoinsModal = ({ isOpen, onClose, userId }) => {
//   const { starCoins, tier } = useUsageLimiter(userId);
//   const [selectedPackage, setSelectedPackage] = useState(null);
//   const [showTransactions, setShowTransactions] = useState(false);
//   const [isProcessing, setIsProcessing] = useState(false);
//   const [packages, setPackages] = useState([]);
//   const [transactions, setTransactions] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState(null);

//   // Load packages and transactions when modal opens
//   useEffect(() => {
//     if (isOpen) {
//       setIsLoading(true);
//       setError(null);
      
//       // Load packages
//       starCoinsClient.getPackages(tier === 'pro')
//         .then(packages => setPackages(packages))
//         .catch(error => {
//           console.error('Error loading packages:', error);
//           setError('Failed to load packages. Please try again.');
//         })
//         .finally(() => setIsLoading(false));
      
//       // Load transaction history
//       starCoinsClient.getTransactionHistory()
//         .then(transactions => setTransactions(transactions))
//         .catch(error => {
//           console.error('Error loading transactions:', error);
//           // Not setting modal error for this since it's not critical
//         });
//     }
//   }, [isOpen, tier]);

//   const handlePurchase = async () => {
//     if (!selectedPackage) return;
    
//     setIsProcessing(true);
//     setError(null);
    
//     try {
//       // Find the selected package
//       const packageData = packages.find(p => p.id === selectedPackage);
//       if (!packageData) {
//         throw new Error('Package not found');
//       }
      
//       // Use the starCoinsClient to create a checkout
//       await starCoinsClient.createCheckout(
//         packageData.id,
//         packageData.amount,
//         packageData.price,
//         tier === 'pro'
//       );
      
//       // Note: We'll be redirected to Stripe checkout, no need to handle success here
//     } catch (error) {
//       console.error('Error processing payment:', error);
//       setIsProcessing(false);
//       setError('Error processing payment. Please try again.');
//     }
//   };
  
//   // Format timestamp for display
//   const formatDate = (timestamp) => {
//     if (!timestamp) return '';
    
//     const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
//     return date.toLocaleDateString();
//   };
  
//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <motion.div
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           exit={{ opacity: 0 }}
//           className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
//           onClick={onClose}
//         >
//           <motion.div
//             initial={{ y: 20, opacity: 0, scale: 0.95 }}
//             animate={{ y: 0, opacity: 1, scale: 1 }}
//             exit={{ y: 20, opacity: 0, scale: 0.95 }}
//             transition={{ type: 'spring', damping: 25, stiffness: 300 }}
//             className="bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-5 w-full max-w-md"
//             onClick={e => e.stopPropagation()}
//           >
//             {/* Header */}
//             <div className="flex items-center justify-between mb-4">
//               <div className="flex items-center gap-2">
//                 <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
//                   <Star className="w-5 h-5 text-amber-400" />
//                 </div>
//                 <div>
//                   <h3 className="text-lg font-medium text-white">Star Coins</h3>
//                   <p className="text-xs text-white/60">Continue using features after reaching limits</p>
//                 </div>
//               </div>
//               <button
//                 onClick={onClose}
//                 className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
//               >
//                 <X className="w-5 h-5" />
//               </button>
//             </div>
            
//             {/* Balance display */}
//             <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-lg p-4 mb-5 border border-amber-500/20">
//               <div className="flex justify-between items-center">
//                 <span className="text-white/70">Current Balance</span>
//                 <div className="flex items-center gap-1.5 text-amber-300 font-medium text-lg">
//                   <Star className="w-4 h-4" />
//                   <span>{starCoins.balance || 0}</span>
//                 </div>
//               </div>
//             </div>
            
//             {/* Error message if any */}
//             {error && (
//               <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
//                 <p className="text-red-400 text-sm">{error}</p>
//               </div>
//             )}
            
//             {/* Tabs */}
//             <div className="flex mb-4 border-b border-slate-700/50">
//               <button
//                 onClick={() => setShowTransactions(false)}
//                 className={`flex-1 py-2 text-sm font-medium relative ${
//                   !showTransactions ? 'text-amber-400' : 'text-slate-400 hover:text-white'
//                 }`}
//               >
//                 Purchase Coins
//                 {!showTransactions && (
//                   <motion.div 
//                     layoutId="tabIndicator"
//                     className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-500 to-orange-500"
//                   />
//                 )}
//               </button>
//               <button
//                 onClick={() => setShowTransactions(true)}
//                 className={`flex-1 py-2 text-sm font-medium relative ${
//                   showTransactions ? 'text-amber-400' : 'text-slate-400 hover:text-white'
//                 }`}
//               >
//                 Transaction History
//                 {showTransactions && (
//                   <motion.div 
//                     layoutId="tabIndicator"
//                     className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-500 to-orange-500"
//                   />
//                 )}
//               </button>
//             </div>
            
//             {/* Content */}
//             <AnimatePresence mode="wait">
//               {!showTransactions ? (
//                 <motion.div
//                   key="purchase"
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   exit={{ opacity: 0, y: -10 }}
//                 >
//                   {isLoading ? (
//                     <div className="flex justify-center py-10">
//                       <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-400"></div>
//                     </div>
//                   ) : (
//                     <>
//                       {/* Package selection */}
//                       <div className="space-y-3 mb-5">
//                         {packages.map(pkg => {
//                           const isActive = selectedPackage === pkg.id;
                          
//                           // Display bonus coins for pro users
//                           const bonusAmount = pkg.proBonus ? pkg.amount - (pkg.originalAmount || pkg.amount * 0.8) : 0;
                          
//                           return (
//                             <div 
//                               key={pkg.id}
//                               className={`relative p-4 rounded-lg transition-all cursor-pointer ${
//                                 isActive 
//                                   ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30' 
//                                   : 'bg-slate-700/30 hover:bg-slate-700/50 border-slate-600/50'
//                               } border`}
//                               onClick={() => setSelectedPackage(pkg.id)}
//                             >
//                               {pkg.popular && (
//                                 <div className="absolute -top-2 right-3 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
//                                   Best Value
//                                 </div>
//                               )}
//                               <div className="flex justify-between items-center">
//                                 <div>
//                                   <h4 className="font-medium text-white">{pkg.name}</h4>
//                                   <div className="flex items-center gap-1 mt-1 text-amber-300">
//                                     <Star className="w-3.5 h-3.5" />
//                                     <span className="font-medium">{pkg.amount} coins</span>
//                                     {bonusAmount > 0 && (
//                                       <span className="text-xs text-emerald-400 ml-1">
//                                         (includes {Math.round(bonusAmount)} bonus)
//                                       </span>
//                                     )}
//                                   </div>
//                                 </div>
//                                 <div className="text-right">
//                                   <div className="text-white font-medium">${pkg.price.toFixed(2)}</div>
//                                   {pkg.proBonus && (
//                                     <div className="text-emerald-400 text-xs">Pro Bonus</div>
//                                   )}
//                                 </div>
//                               </div>
                              
//                               {isActive && (
//                                 <div className="absolute -right-1 -bottom-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
//                                   <Check className="w-3 h-3 text-white" />
//                                 </div>
//                               )}
//                             </div>
//                           );
//                         })}
//                       </div>
                      
//                       {/* Purchase button */}
//                       <button
//                         onClick={handlePurchase}
//                         disabled={!selectedPackage || isProcessing}
//                         className={`w-full py-3 rounded-lg flex items-center justify-center gap-2 ${
//                           selectedPackage && !isProcessing
//                             ? 'bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white'
//                             : 'bg-slate-700/50 text-slate-400 cursor-not-allowed'
//                         }`}
//                       >
//                         {isProcessing ? (
//                           <>
//                             <RefreshCcw className="w-4 h-4 animate-spin" />
//                             <span>Processing...</span>
//                           </>
//                         ) : (
//                           <>
//                             <CreditCard className="w-4 h-4" />
//                             <span>Purchase</span>
//                           </>
//                         )}
//                       </button>
//                     </>
//                   )}
//                 </motion.div>
//               ) : (
//                 <motion.div
//                   key="transactions"
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   exit={{ opacity: 0, y: -10 }}
//                   className="max-h-60 overflow-y-auto pr-1"
//                 >
//                   {transactions.length > 0 ? (
//                     <div className="space-y-2">
//                       {transactions.map((transaction, index) => (
//                         <div key={transaction.id || index} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
//                           <div>
//                             <div className="flex items-center gap-1.5">
//                               <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
//                                 transaction.amount > 0 
//                                   ? 'bg-emerald-500/20 text-emerald-300' 
//                                   : 'bg-red-500/20 text-red-300'
//                               }`}>
//                                 {transaction.amount > 0 ? '+' : '-'}
//                               </div>
//                               <span className="text-white font-medium">
//                                 {Math.abs(transaction.amount)} coins
//                               </span>
//                             </div>
//                             <div className="text-xs text-white/50 mt-1">
//                               {transaction.feature || transaction.source || 'System'}
//                             </div>
//                           </div>
                          
//                           <div className="text-right">
//                             <div className="text-xs text-white/50">
//                               {formatDate(transaction.timestamp)}
//                             </div>
//                             <div className="text-xs text-white/70 mt-1">
//                               Balance: {transaction.balance}
//                             </div>
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   ) : (
//                     <div className="text-center py-6 text-white/50">
//                       No transactions yet
//                     </div>
//                   )}
//                 </motion.div>
//               )}
//             </AnimatePresence>
//           </motion.div>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// };

// export default StarCoinsModal;

// // StarCoinsModal.jsx
// import React, { useState, useEffect } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { X, Star, CreditCard, Check, ChevronDown, ChevronUp, History, RefreshCcw } from 'lucide-react';
// import { useUsageLimiter } from '../../utils/usageLimiter';
// import { stripeService } from '../../services/stripeService';

// const StarCoinsModal = ({ isOpen, onClose, userId }) => {
//   const { starCoins, tier } = useUsageLimiter(userId);
//   const [selectedPackage, setSelectedPackage] = useState(null);
//   const [showTransactions, setShowTransactions] = useState(false);
//   const [isProcessing, setIsProcessing] = useState(false);

//   useEffect(() => {
//     if (isOpen) {
//       starCoins.loadTransactions();
//     }
//   }, [isOpen, starCoins]);

//   const handlePurchase = async () => {
//     if (!selectedPackage) return;
    
//     setIsProcessing(true);
    
//     try {
//       // Get the selected package details
//       const isPro = tier === 'pro';
//       const packageData = starCoins.packages.find(p => p.id === selectedPackage);
      
//       // Determine the correct amount of coins based on user tier
//       const coinsToAdd = isPro && packageData.proAmount ? packageData.proAmount : packageData.amount;
//       const price = isPro ? packageData.proPrice : packageData.price;
      
//       // Use the Stripe service to create a checkout session
//       await stripeService.createCheckoutSession(
//         userId,
//         selectedPackage,
//         coinsToAdd, // Pass the correct amount of coins
//         price
//       );
      
//       // Note: The actual coin addition will happen after successful Stripe checkout
//       // This is handled by the webhook that processes successful payments
      
//       // Note: We don't need to set success state here as we'll be redirected to Stripe
//     } catch (error) {
//       console.error('Error processing payment:', error);
//       setIsProcessing(false);
//       alert('Error processing payment. Please try again.');
//     }
//   };
  
//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <motion.div
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           exit={{ opacity: 0 }}
//           className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
//           onClick={onClose}
//         >
//           <motion.div
//             initial={{ y: 20, opacity: 0, scale: 0.95 }}
//             animate={{ y: 0, opacity: 1, scale: 1 }}
//             exit={{ y: 20, opacity: 0, scale: 0.95 }}
//             transition={{ type: 'spring', damping: 25, stiffness: 300 }}
//             className="bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-5 w-full max-w-md"
//             onClick={e => e.stopPropagation()}
//           >
//             {/* Header */}
//             <div className="flex items-center justify-between mb-4">
//               <div className="flex items-center gap-2">
//                 <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
//                   <Star className="w-5 h-5 text-amber-400" />
//                 </div>
//                 <div>
//                   <h3 className="text-lg font-medium text-white">Star Coins</h3>
//                   <p className="text-xs text-white/60">Continue using features after reaching limits</p>
//                 </div>
//               </div>
//               <button
//                 onClick={onClose}
//                 className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
//               >
//                 <X className="w-5 h-5" />
//               </button>
//             </div>
            
//             {/* Balance display */}
//             <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-lg p-4 mb-5 border border-amber-500/20">
//               <div className="flex justify-between items-center">
//                 <span className="text-white/70">Current Balance</span>
//                 <div className="flex items-center gap-1.5 text-amber-300 font-medium text-lg">
//                   <Star className="w-4 h-4" />
//                   <span>{starCoins.balance}</span>
//                 </div>
//               </div>
//             </div>
            
//             {/* Tabs */}
//             <div className="flex mb-4 border-b border-slate-700/50">
//               <button
//                 onClick={() => setShowTransactions(false)}
//                 className={`flex-1 py-2 text-sm font-medium relative ${
//                   !showTransactions ? 'text-amber-400' : 'text-slate-400 hover:text-white'
//                 }`}
//               >
//                 Purchase Coins
//                 {!showTransactions && (
//                   <motion.div 
//                     layoutId="tabIndicator"
//                     className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-500 to-orange-500"
//                   />
//                 )}
//               </button>
//               <button
//                 onClick={() => setShowTransactions(true)}
//                 className={`flex-1 py-2 text-sm font-medium relative ${
//                   showTransactions ? 'text-amber-400' : 'text-slate-400 hover:text-white'
//                 }`}
//               >
//                 Transaction History
//                 {showTransactions && (
//                   <motion.div 
//                     layoutId="tabIndicator"
//                     className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-500 to-orange-500"
//                   />
//                 )}
//               </button>
//             </div>
            
//             {/* Content */}
//             <AnimatePresence mode="wait">
//               {!showTransactions ? (
//                 <motion.div
//                   key="purchase"
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   exit={{ opacity: 0, y: -10 }}
//                 >
//                   {/* Package selection */}
//                   <div className="space-y-3 mb-5">
//                     {starCoins.packages.map(pkg => {
//                       const isActive = selectedPackage === pkg.id;
//                       const price = tier === 'pro' ? pkg.proPrice : pkg.price;
                      
//                       // Display bonus coins for pro users
//                       const displayAmount = tier === 'pro' && pkg.proAmount ? pkg.proAmount : pkg.amount;
//                       const bonusAmount = tier === 'pro' && pkg.proAmount ? pkg.proAmount - pkg.amount : 0;
                      
//                       return (
//                         <div 
//                           key={pkg.id}
//                           className={`relative p-4 rounded-lg transition-all cursor-pointer ${
//                             isActive 
//                               ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30' 
//                               : 'bg-slate-700/30 hover:bg-slate-700/50 border-slate-600/50'
//                           } border`}
//                           onClick={() => setSelectedPackage(pkg.id)}
//                         >
//                           {pkg.popular && (
//                             <div className="absolute -top-2 right-3 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
//                               Best Value
//                             </div>
//                           )}
//                           <div className="flex justify-between items-center">
//                             <div>
//                               <h4 className="font-medium text-white">{pkg.name}</h4>
//                               <div className="flex items-center gap-1 mt-1 text-amber-300">
//                                 <Star className="w-3.5 h-3.5" />
//                                 <span className="font-medium">{displayAmount} coins</span>
//                                 {bonusAmount > 0 && (
//                                   <span className="text-xs text-emerald-400 ml-1">
//                                     (includes {bonusAmount} bonus)
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                             <div className="text-right">
//                               <div className="text-white font-medium">${price.toFixed(2)}</div>
//                               {tier === 'pro' && (
//                                 <div className="text-emerald-400 text-xs">Pro Price</div>
//                               )}
//                             </div>
//                           </div>
                          
//                           {isActive && (
//                             <div className="absolute -right-1 -bottom-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
//                               <Check className="w-3 h-3 text-white" />
//                             </div>
//                           )}
//                         </div>
//                       );
//                     })}
//                   </div>
                  
//                   {/* Purchase button */}
//                   <button
//                     onClick={handlePurchase}
//                     disabled={!selectedPackage || isProcessing}
//                     className={`w-full py-3 rounded-lg flex items-center justify-center gap-2 ${
//                       selectedPackage && !isProcessing
//                         ? 'bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white'
//                         : 'bg-slate-700/50 text-slate-400 cursor-not-allowed'
//                     }`}
//                   >
//                     {isProcessing ? (
//                       <>
//                         <RefreshCcw className="w-4 h-4 animate-spin" />
//                         <span>Processing...</span>
//                       </>
//                     ) : (
//                       <>
//                         <CreditCard className="w-4 h-4" />
//                         <span>Purchase</span>
//                       </>
//                     )}
//                   </button>
//                 </motion.div>
//               ) : (
//                 <motion.div
//                   key="transactions"
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   exit={{ opacity: 0, y: -10 }}
//                   className="max-h-60 overflow-y-auto pr-1"
//                 >
//                   {starCoins.transactions.length > 0 ? (
//                     <div className="space-y-2">
//                       {starCoins.transactions.map(transaction => (
//                         <div key={transaction.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
//                           <div>
//                             <div className="flex items-center gap-1.5">
//                               <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
//                                 transaction.amount > 0 
//                                   ? 'bg-emerald-500/20 text-emerald-300' 
//                                   : 'bg-red-500/20 text-red-300'
//                               }`}>
//                                 {transaction.amount > 0 ? '+' : '-'}
//                               </div>
//                               <span className="text-white font-medium">
//                                 {Math.abs(transaction.amount)} coins
//                               </span>
//                             </div>
//                             <div className="text-xs text-white/50 mt-1">
//                               {transaction.feature || transaction.source || 'System'}
//                             </div>
//                           </div>
                          
//                           <div className="text-right">
//                             <div className="text-xs text-white/50">
//                               {new Date(transaction.timestamp.toDate()).toLocaleDateString()}
//                             </div>
//                             <div className="text-xs text-white/70 mt-1">
//                               Balance: {transaction.balance}
//                             </div>
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   ) : (
//                     <div className="text-center py-6 text-white/50">
//                       No transactions yet
//                     </div>
//                   )}
//                 </motion.div>
//               )}
//             </AnimatePresence>
//           </motion.div>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// };

// export default StarCoinsModal;